import classnames from 'classnames';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { AccordionClosedIcon, AccordionOpenIcon } from '../../common/icons';
import { infoIcon } from '../competency-bars/common';

const styles = require('./manager-report-table.css');

type RowCollectionProps = {
  key: string;
  sub?: SubRowProps[];
} & CommonRowProps;

type SubRowProps = {
  tooltip: string;
} & CommonRowProps;

interface CommonRowProps {
  header: string;
  content: (string | JSX.Element)[];
}

export interface ManagerReportTableProps {
  columns: string[];
  content: {
    main: RowCollectionProps[];
  };
}

type Row =
  | {
    kind: 'main';
    onClick(): void;
  } & CommonRow
  | {
    kind: 'sub';
    tooltip?: string;
    onMouseEnter(): void;
    onMouseLeave(): void;
  } & CommonRow
  | {
    kind: 'titles';
  } & CommonRow;

interface CommonRow {
  header: string;
  values: (string | JSX.Element)[];
  isOpen: boolean;
}

@observer
export class ManagerReportTable extends React.Component<ManagerReportTableProps> {
  @observable
  openRowsMap: { [key: string]: boolean } = {};

  @observable
  tooltipShowing:
    | {
      tooltip: string;
      mainKey: string;
    }
    | undefined = undefined;

  rowIsOpen(key: string): boolean {
    return this.openRowsMap[key];
  }

  @action
  onClickRow = (key: string) => {
    this.openRowsMap[key] = !this.openRowsMap[key];
  };

  @action
  onMouseEnterTooltip = (tooltip: string, mainKey: string) => {
    this.tooltipShowing = { tooltip, mainKey };
  };

  @action
  onMouseLeaveTooltip = () => {
    this.tooltipShowing = undefined;
  };

  tooltipIsShowing(tooltip: string, mainKey: string) {
    return (
      this.tooltipShowing !== undefined &&
      this.tooltipShowing.tooltip === tooltip &&
      this.tooltipShowing.mainKey === mainKey
    );
  }

  renderHeader(r: Row, key: number) {
    if (r.kind === 'titles') {
      return undefined;
    } else if (r.kind === 'main') {
      return (
        <>
          <div data-tour={`table-row:${key}`} className={styles.expander}>
            {r.isOpen ? (
              <AccordionOpenIcon size="small" color="black" />
            ) : (
                <AccordionClosedIcon size="small" color="black" />
              )}
          </div>
          <div className={classnames(styles.reportTableHeadingTitle, r.isOpen ? styles.underline : undefined)}>
            {r.header}
          </div>
        </>
      );
    }
    return (
      <>
        <div className={styles.reportTableHeadingIcon}>
          {r.tooltip && <div className={classnames(styles.tooltip)}>{r.tooltip}</div>}
          {/* <InfoIcon size="small" color="orange" onMouseEnter={r.onMouseEnter} onMouseLeave={r.onMouseLeave} /> */}
          {infoIcon({ key, onMouseEnter: r.onMouseEnter, onMouseLeave: r.onMouseLeave, width: 16, height: 16 })}
        </div>
        <div className={styles.reportTableHeadingTitle}>{r.header}</div>
      </>
    );
  }

  renderRow(r: Row, key: number) {
    return (
      <div
        key={key}
        className={classnames(
          styles.reportTableRow,
          styles[r.kind],
          r.kind === 'sub' && !r.isOpen ? styles.hidden : undefined
        )}
        onClick={r.kind === 'main' ? r.onClick : undefined}
      >
        <div
          className={styles.reportTableHeading}
          onMouseEnter={r.kind === 'sub' ? r.onMouseEnter : undefined}
          onMouseLeave={r.kind === 'sub' ? r.onMouseLeave : undefined}
        >
          {this.renderHeader(r, key)}
        </div>
        <div className={styles.reportTableRowContent}>
          {r.values.map((s, index) => (
            <div
              key={index}
              className={classnames(
                styles.reportTableCell,
                r.kind === 'titles' ? styles.reportTableHeadingTitle : undefined
              )}
            >
              {s}
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderRowCollection = (rowProps: RowCollectionProps, key: number) => {
    const isOpen = this.rowIsOpen(rowProps.key);
    const header = rowProps.header;
    return (
      <>
        {this.renderRow(
          { kind: 'main', onClick: () => this.onClickRow(rowProps.key), isOpen, header, values: rowProps.content },
          key
        )}
        {rowProps.sub &&
          rowProps.sub.map((s, index) =>
            this.renderRow(
              {
                kind: 'sub',
                isOpen,
                header: s.header,
                values: s.content,
                onMouseEnter: () => this.onMouseEnterTooltip(s.tooltip, rowProps.key),
                onMouseLeave: this.onMouseLeaveTooltip,
                tooltip: this.tooltipIsShowing(s.tooltip, rowProps.key) ? s.tooltip : undefined
              },
              index
            )
          )}
      </>
    );
  };
  render() {
    return (
      <div className={styles.container}>
        <div className={styles.reportTable}>
          {this.renderRow({ kind: 'titles', header: '', values: this.props.columns, isOpen: true }, 0)}
          {this.props.content.main.map((m, index) => this.renderRowCollection(m, index))}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/ui/components/manager-report-table/manager-report-table.tsx